/**
 * ChecklistItemsView.js
 *
 * Read-only list view of checklist items for a single category.
 * No toggles - just displays items with edit/import actions.
 */

import React from 'react';
import { Lock, FileText, FileUp, Pencil, AlertCircle } from 'lucide-react';

/**
 * Single item row
 * NOTE: 'required' means "must complete during execution" not "cannot be customized"
 */
const ChecklistItemRow = ({ item, index }) => {
  const isRequired = item.required || item.regulation;
  const isLocked = item.locked === true;

  return (
    <div className={`
      flex items-center justify-between py-3 px-4
      ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
      ${isRequired ? 'border-l-4 border-l-amber-400' : ''}
    `}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {isLocked && (
          <Lock className="h-4 w-4 text-amber-500 flex-shrink-0" />
        )}
        <span className="text-slate-800 truncate">{item.text}</span>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {item.regulation && (
          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
            {item.regulation}
          </span>
        )}
        {item.source === 'custom' && (
          <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded">
            Custom
          </span>
        )}
        {item.source === 'imported' && (
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
            Imported
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Main component
 */
const ChecklistItemsView = ({
  items = [],
  categoryLabel,
  onEdit,
  onImport,
  lastModified,
  modifiedBy,
  canEdit = true
}) => {
  const requiredItems = items.filter(i => i.required || i.regulation);
  const standardItems = items.filter(i => !i.required && !i.regulation && i.source === 'library');
  const customItems = items.filter(i => i.source === 'custom' || i.source === 'imported');

  return (
    <div className="space-y-4">
      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          {items.length} items total
          {requiredItems.length > 0 && ` (${requiredItems.length} required)`}
        </div>

        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <button
                onClick={onEdit}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium
                  text-slate-700 bg-white border border-slate-300 rounded-lg
                  hover:bg-slate-50 transition-colors"
              >
                <Pencil className="h-4 w-4" />
                Edit Checklist
              </button>
              <button
                onClick={onImport}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium
                  text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-lg
                  hover:bg-cyan-100 transition-colors"
              >
                <FileUp className="h-4 w-4" />
                Import from SMS
                <span className="text-xs bg-cyan-200 px-1.5 py-0.5 rounded">PRO</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Core Items Section */}
      {requiredItems.length > 0 && (
        <div className="border border-amber-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-900">
                Core Items ({requiredItems.length})
              </span>
            </div>
            <p className="text-xs text-amber-700 mt-1">
              Regulatory or safety-critical items. Items with a lock icon cannot be modified.
            </p>
          </div>
          <div className="divide-y divide-amber-100">
            {requiredItems.map((item, index) => (
              <ChecklistItemRow key={item.id} item={item} index={index} />
            ))}
          </div>
        </div>
      )}

      {/* Standard Items Section */}
      {standardItems.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
            <span className="text-sm font-medium text-slate-800">
              Standard Checks ({standardItems.length})
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {standardItems.map((item, index) => (
              <ChecklistItemRow key={item.id} item={item} index={index} />
            ))}
          </div>
        </div>
      )}

      {/* Custom/Imported Items Section */}
      {customItems.length > 0 && (
        <div className="border border-cyan-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-cyan-50 border-b border-cyan-200">
            <span className="text-sm font-medium text-cyan-900">
              Custom & Imported Checks ({customItems.length})
            </span>
          </div>
          <div className="divide-y divide-cyan-100">
            {customItems.map((item, index) => (
              <ChecklistItemRow key={item.id} item={item} index={index} />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {items.length === 0 && (
        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">No items configured</p>
          <p className="text-sm text-slate-500 mt-1">
            Click "Edit Checklist" to add items or "Import from SMS" to extract from documents.
          </p>
        </div>
      )}

      {/* Last Modified Info */}
      {lastModified && (
        <div className="text-xs text-slate-500 text-right">
          Last modified: {new Date(lastModified).toLocaleDateString('en-GB')}
          {modifiedBy?.name && ` by ${modifiedBy.name}`}
        </div>
      )}
    </div>
  );
};

export default ChecklistItemsView;
